// SOLICITUD IA · panel ÚNICO de revisión (barra lateral, hoja móvil y enlace directo).
// PC1 intacto: el backend sigue siendo la autoridad (revisor distinto del proponente, TTL de 10
// minutos, inicio/fin fijados al preparar, revalidación, concurrencia e idempotencia). Aquí sólo
// se añade una CONFIRMACIÓN DE INTERFAZ para evitar toques accidentales en pantallas táctiles.
import { useState, useEffect } from "react";
import { ShieldCheck, Clock, Loader2, Share2, AlertTriangle } from "lucide-react";

const P = (v) => (v || "").toUpperCase();

export const EstadoPill = ({ estado }) => (
  <span data-testid="solicitud-estado" className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${
    estado === "aplicada" ? "bg-emerald-100 text-emerald-800"
      : estado === "pendiente" ? "bg-[#01CBE3]/20 text-[#0891A6]"
      : estado === "rechazada" ? "bg-[#9B2C2C]/15 text-[#9B2C2C]"
      : "bg-slate-200 text-slate-600"}`}>{estado}</span>
);

const minutosRestantes = (expira) => {
  if (!expira) return null;
  const ms = new Date(expira).getTime() - Date.now();
  return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / 60000));
};

// Minutos que le quedan a la propuesta, vivos mientras la pantalla siga abierta.
function useMinutosRestantes(expira) {
  const [mins, setMins] = useState(minutosRestantes(expira));
  useEffect(() => {
    setMins(minutosRestantes(expira));
    const t = setInterval(() => setMins(minutosRestantes(expira)), 15000);
    return () => clearInterval(t);
  }, [expira]);
  return mins;
}

export function DetalleSolicitud({ s, prefix }) {
  const par = s.params || {};
  const snap = s.snapshot || {};
  const mins = useMinutosRestantes(s.expira_en);
  return (
    <dl className="mt-2 space-y-1 text-xs text-slate-700 dark:text-slate-200">
      <div><dt className="inline font-semibold">Persona: </dt><dd data-testid={`${prefix}-persona`} className="inline">{snap.persona_nombre}{snap.habitacion ? ` · ${snap.habitacion}` : ""}</dd></div>
      {(s.sitio_nombre || snap.sitio_nombre) && (
        <div><dt className="inline font-semibold">Sitio: </dt><dd data-testid={`${prefix}-sitio`} className="inline">{s.sitio_nombre || snap.sitio_nombre}</dd></div>
      )}
      <div><dt className="inline font-semibold">Evento: </dt><dd data-testid={`${prefix}-evento`} className="inline">{par.tipo_evento}</dd></div>
      <div><dt className="inline font-semibold">Prioridad: </dt><dd data-testid={`${prefix}-cambio`} className="inline">{P(snap.prioridad_antes)} → {P(par.prioridad)}</dd></div>
      <div><dt className="inline font-semibold">Vigencia: </dt><dd data-testid={`${prefix}-vigencia`} className="inline">{par.inicio} → {par.fin} ({snap.zona_horaria})</dd></div>
      <div><dt className="inline font-semibold">Duración: </dt><dd data-testid={`${prefix}-duracion`} className="inline">{snap.duracion_txt}</dd></div>
      {par.motivo && <div><dt className="inline font-semibold">Motivo: </dt><dd data-testid={`${prefix}-motivo`} className="inline">{par.motivo}</dd></div>}
      <div><dt className="inline font-semibold">Solicitó: </dt><dd data-testid={`${prefix}-proponente`} className="inline">{s.actor_email} ({s.actor_rol})</dd></div>
      <div className="flex items-center gap-1.5"><dt className="font-semibold">Estado: </dt><dd><EstadoPill estado={s.estado_display || s.estado} /></dd></div>
      {s.estado === "pendiente" && mins !== null && (
        <p data-testid={`${prefix}-vencimiento`} className="!mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
          <Clock className="h-3 w-3" /> {mins > 0 ? `Caduca en ${mins} min` : "Caducada: ya no puede confirmarse"}
        </p>
      )}
      {(snap.avisos || []).map((a, i) => (
        <p key={i} data-testid={`${prefix}-aviso-${i}`} className="!mt-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-900 dark:bg-amber-900/25 dark:text-amber-100">{a}</p>
      ))}
    </dl>
  );
}

// Acciones de revisión con confirmación de interfaz. `onConfirmar`/`onRechazar` hacen la llamada.
export default function SolicitudPanel({ s, prefix = "ia-rev", aplicando, onConfirmar, onRechazar,
  revisorTxt = "otra persona autorizada", compartible = true }) {
  const [paso, setPaso] = useState(null);     // null | "confirmar" | "rechazar"
  const [motivo, setMotivo] = useState("");
  const [copiado, setCopiado] = useState(false);
  // GC92-P2 · si la propuesta CADUCA mientras la pantalla sigue abierta, «Confirmar» se bloquea
  // en la interfaz para no invitar a un toque inútil. El servidor sigue siendo la autoridad: PC1
  // y su TTL no se tocan, y un intento tardío seguiría rechazándose en el backend.
  const mins = useMinutosRestantes(s?.expira_en);
  if (!s) return null;
  const vencidaEnPantalla = s.estado === "pendiente" && mins === 0;
  const decidible = !!s.puede_decidir && !vencidaEnPantalla;
  const enlace = `${window.location.origin}/solicitud/${s.id}`;

  const compartir = async () => {
    // Identificador OPACO en la URL: sin nombre, sin sitio y sin el hash de autorización. Quien
    // abra el enlace necesita su propia sesión y su propia autorización efectiva.
    const texto = `Solicitud de cambio temporal de prioridad pendiente de revisión en Galarya: ${enlace}`;
    try {
      if (navigator.share) { await navigator.share({ text: texto }); return; }
    } catch { /* el usuario canceló: se ofrece WhatsApp igualmente */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank", "noopener");
    try { await navigator.clipboard.writeText(enlace); setCopiado(true); } catch { /* sin portapapeles */ }
  };

  return (
    <div data-testid="ia-revision-detalle">
      <DetalleSolicitud s={s} prefix={prefix} />

      {compartible && s.estado === "pendiente" && (
        <button data-testid="ia-revision-compartir" type="button" onClick={compartir}
          className="mt-2.5 inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-[#01CBE3]/60 px-3 py-1.5 text-xs font-bold text-[#0891A6] hover:bg-[#01CBE3]/10">
          <Share2 className="h-3.5 w-3.5" /> Compartir enlace {copiado ? "· copiado" : ""}
        </button>
      )}

      {s.puede_decidir ? (
        <>
          {paso === null && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              <button data-testid="ia-revision-confirmar" onClick={() => setPaso("confirmar")}
                disabled={aplicando || !decidible}
                title={vencidaEnPantalla ? "La propuesta caducó: ya no puede aplicarse." : "Confirmar el cambio temporal"}
                className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-[#012472] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#012472]/90 disabled:cursor-not-allowed disabled:opacity-50">
                <ShieldCheck className="h-3.5 w-3.5" /> Confirmar
              </button>
              <button data-testid="ia-revision-rechazar" onClick={() => setPaso("rechazar")} disabled={aplicando}
                className="min-h-10 rounded-lg border border-[#9B2C2C]/40 px-3 py-1.5 text-xs font-bold text-[#9B2C2C] hover:bg-[#9B2C2C]/10 disabled:opacity-50">
                Rechazar
              </button>
            </div>
          )}
          {vencidaEnPantalla && (
            <p data-testid="ia-revision-caducada" className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-900/25 dark:text-amber-100">
              <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              La propuesta caducó mientras esta pantalla estaba abierta. Pide que se prepare de nuevo.
            </p>
          )}
          {paso === "confirmar" && (
            <div data-testid="ia-revision-confirmar-paso2" className="mt-2.5 rounded-lg border border-[#012472]/30 bg-white p-2.5 dark:bg-slate-900">
              <p className="text-[11px] font-semibold leading-relaxed text-slate-700 dark:text-slate-200">
                Vas a aplicar el cambio temporal de prioridad tal como está descrito. ¿Confirmas?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="ia-revision-confirmar-si" onClick={() => onConfirmar?.()} disabled={aplicando || !decidible}
                  className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-[#012472] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#012472]/90 disabled:cursor-not-allowed disabled:opacity-50">
                  {aplicando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} {aplicando ? "Aplicando…" : "Sí, aplicar"}
                </button>
                <button data-testid="ia-revision-confirmar-no" onClick={() => setPaso(null)} disabled={aplicando}
                  className="min-h-10 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">Cancelar</button>
              </div>
            </div>
          )}
          {paso === "rechazar" && (
            <div data-testid="ia-revision-rechazar-paso2" className="mt-2.5 rounded-lg border border-[#9B2C2C]/30 bg-white p-2.5 dark:bg-slate-900">
              <input data-testid="ia-revision-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="Motivo (opcional)"
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-700 outline-none focus:border-[#01CBE3] dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="ia-revision-rechazar-si" onClick={() => onRechazar?.(motivo)} disabled={aplicando}
                  className="min-h-10 rounded-lg border border-[#9B2C2C]/40 px-3 py-1.5 text-xs font-bold text-[#9B2C2C] hover:bg-[#9B2C2C]/10 disabled:opacity-50">
                  Sí, rechazar
                </button>
                <button data-testid="ia-revision-rechazar-no" onClick={() => setPaso(null)} disabled={aplicando}
                  className="min-h-10 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">Cancelar</button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p data-testid="ia-revision-no-decidible" className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-slate-100 px-2 py-1.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {s.propia ? `Es tu propia solicitud: debe decidirla ${revisorTxt}.`
            : "Esta solicitud ya no es aprobable (vencida, decidida o invalidada)."}
        </p>
      )}
    </div>
  );
}
